import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { formatMinorAmount } from "@/core/utils/func/money-minor";
import type { MerchantSaleDto } from "@/features/merchant/api/dto";
import {
  SALE_STATUSES,
  type SaleStatus,
} from "@/features/shared/api/enums";

export type MerchantSaleRow = MerchantSaleDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  SaleStatus,
  "warning" | "success" | "destructive"
> = {
  PENDING: "warning",
  SETTLED: "success",
  REVERSED: "destructive",
};

/**
 * Colonnes des ventes marchandes (`POST /MerchantSales/all`) — montants et
 * identifiants en `NoTranslate` (règle notranslate sur données financières).
 */
export const merchantSalesColumns: ColumnsClarFactory<MerchantSaleRow> = () => [
  {
    id: "id",
    accessorKey: "id",
    header: t("merchant.sales.columns.reference"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "merchantId",
    accessorKey: "merchantId",
    header: t("merchant.sales.columns.merchantId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "salesAgentId",
    accessorKey: "salesAgentId",
    header: t("merchant.sales.columns.salesAgentId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "amountMinor",
    accessorKey: "amountMinor",
    header: t("merchant.sales.columns.amount"),
    cell: ({ row }) => (
      <NoTranslate as="span" className="font-medium">
        {row.original.amountMinor != null
          ? formatMinorAmount(
              row.original.amountMinor,
              row.original.currency ?? undefined
            )
          : "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "feeAmountMinor",
    accessorKey: "feeAmountMinor",
    header: t("merchant.sales.columns.fee"),
    cell: ({ row }) => (
      <NoTranslate as="span">
        {row.original.feeAmountMinor != null
          ? formatMinorAmount(
              row.original.feeAmountMinor,
              row.original.currency ?? undefined
            )
          : "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "currency",
    accessorKey: "currency",
    header: t("merchant.sales.columns.currency"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("merchant.sales.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as SaleStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(SALE_STATUSES) },
  },
  {
    id: "occurredAt",
    accessorKey: "occurredAt",
    header: t("merchant.sales.columns.occurredAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        {formatDateWithFallback((getValue() as string) ?? "", "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
];
